import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * "Block this number" — History's conversation detail and Live (plan-029 unit **WAPP-HIST1**; design §40.5, §40.13.1,
 * §40.19.2; FR-CP-4; ADR 0081, proposed).
 *
 * Offered only where the conversation's `may_block` is true: a phone conversation whose caller is known, read by a person
 * who may change what the workspace's VoiceAgents answer. A confirm dialog asks which agents refuse the number (this one,
 * or every agent of the workspace), for how long (a day, a week, 30 days, until removed) and whether it also goes on Do
 * not call (an explicit second act, off until ticked). The request names the CONVERSATION; the number never crosses — the
 * server reads it from the conversation — and the answer says the last three digits at most ("Number ending …123 is
 * blocked."). A refusal is said as the blocked-number panel says it (`block_unavailable`, `caller_protection_unavailable`,
 * the limit). The call is WAPP-PROT's `blockConversationNumber`; a block refreshes that panel's list.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Segmented } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "./block-number-dialog.css";

import { useGateway } from "../../../api/provider.js";
import { conversationNumberBlockTextOf } from "../../../api/party-protection-view.js";
import {
  BLOCKED_NUMBER_QUERY_KEY,
  type BlockedNumberAddedView,
  type BlockedNumberExpiryHour,
  type BlockedNumberScope,
  type ConversationNumberBlock,
} from "../../../api/slice/blocked-number.js";
import { blockedNumberRefusalTextOf } from "./channels/blocked-number-panel.js";

type ScopeValue = BlockedNumberScope;
type ExpiryValue = "24" | "168" | "720" | "never";

const SCOPE_OPTION = [
  { value: "one_voice_agent", get label() { return portalText("callerProtection", "block_this.scope_this"); } },
  { value: "every_voice_agent", get label() { return portalText("callerProtection", "block_this.scope_every"); } },
] as const satisfies readonly { readonly value: ScopeValue; readonly label: string }[];

const EXPIRY_OPTION = [
  { value: "24", get label() { return portalText("callerProtection", "block_this.expiry_day"); } },
  { value: "168", get label() { return portalText("callerProtection", "block_this.expiry_week"); } },
  { value: "720", get label() { return portalText("callerProtection", "block_this.expiry_month"); } },
  { value: "never", get label() { return portalText("callerProtection", "block_this.expiry_never"); } },
] as const satisfies readonly { readonly value: ExpiryValue; readonly label: string }[];

/** The dialog's choices as the request names them. */
export function blockRequestOf(
  voiceAgentId: string,
  scope: ScopeValue,
  expiry: ExpiryValue,
  alsoDoNotCall: boolean,
): ConversationNumberBlock {
  return {
    label: null,
    scope,
    voiceAgentId: scope === "one_voice_agent" ? voiceAgentId : null,
    expiryHour: expiry === "never" ? null : (Number(expiry) as BlockedNumberExpiryHour),
    alsoDoNotCall,
  };
}

export interface BlockThisNumberProps {
  readonly conversationId: string;
  /** The VoiceAgent whose History or Live this is — "This agent". */
  readonly voiceAgentId: string;
}

/** The action and its confirm dialog; the outcome is said beside the button. */
export function BlockThisNumber({ conversationId, voiceAgentId }: BlockThisNumberProps): ReactNode {
  usePortalTranslation("callerProtection");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<ScopeValue>("one_voice_agent");
  const [expiry, setExpiry] = useState<ExpiryValue>("never");
  const [alsoDoNotCall, setAlsoDoNotCall] = useState(false);
  const [done, setDone] = useState<BlockedNumberAddedView | null>(null);

  const block = useMutation({
    mutationFn: () =>
      gateway.blockConversationNumber(conversationId, blockRequestOf(voiceAgentId, scope, expiry, alsoDoNotCall)),
    onSuccess: async (added) => {
      setDone(added);
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: BLOCKED_NUMBER_QUERY_KEY.every });
    },
  });

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          block.reset();
          setOpen(true);
        }}
      >
        {portalText("callerProtection", "block_this.action")}
      </Button>
      <span className="c-va-history-copy-status" role="status" aria-live="polite">
        {done === null ? "" : conversationNumberBlockTextOf(done)}
      </span>
      {open ? (
        <Dialog
          open
          title={portalText("callerProtection", "block_this.title")}
          onClose={() => {
            setOpen(false);
          }}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setOpen(false);
                }}
              >
                {portalText("callerProtection", "block_this.cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={block.isPending}
                onClick={() => {
                  block.mutate();
                }}
              >
                {block.isPending
                  ? portalText("callerProtection", "block_this.working")
                  : portalText("callerProtection", "block_this.confirm")}
              </Button>
            </>
          }
        >
          <div className="bn-form">
            <p className="c-note">{portalText("callerProtection", "block_this.body")}</p>
            <div className="bn-choice">
              <span className="bn-legend">{portalText("callerProtection", "block_this.scope")}</span>
              <Segmented
                name="blockScope"
                legend={portalText("callerProtection", "block_this.scope")}
                value={scope}
                options={SCOPE_OPTION}
                onChange={setScope}
              />
            </div>
            <div className="bn-choice">
              <span className="bn-legend">{portalText("callerProtection", "block_this.expiry")}</span>
              <Segmented
                name="blockExpiry"
                legend={portalText("callerProtection", "block_this.expiry")}
                value={expiry}
                options={EXPIRY_OPTION}
                onChange={setExpiry}
              />
            </div>
            <label className="bn-check">
              <input
                type="checkbox"
                checked={alsoDoNotCall}
                onChange={(event) => {
                  setAlsoDoNotCall(event.currentTarget.checked);
                }}
              />
              <span>{portalText("callerProtection", "block_this.also_do_not_call")}</span>
            </label>
            {block.isError ? (
              <p className="c-note c-note-critical" role="alert">
                {blockedNumberRefusalTextOf(block.error)}
              </p>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}
